/* eslint-disable react/no-this-in-sfc */
import React, { useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// Constants
import { COUNTRIES_LIST } from 'common/constants/countries'
import { BOOLEAN_OPTIONS, NATIONAL_ID_TYPE_INDIVIDUAL_OPTIONS } from 'common/constants/formOptions'

// Store
import { actions } from 'core/store'

// Hooks
import { useLocalStorage } from 'core/hooks/storage'
import { useCreateAccountIndividual, useUpdateAccountIndividual } from 'core/hooks/api'

// Styled Elements
import {
  FormWrapper,
  FormContainer,
  FormInputGroupItem,
  FormHeader,
  FormTitle,
  FormError,
  FormFooter,
  TableButtonWrapper,
  TableRowWrapper,
  TablePanelWrapper,
} from '../DirectoryForms.elements'

// Views
import {
  Form,
  FormSelect,
  FormTextField,
  FormTextArea,
  FormSearchSelect,
  FormDatePicker,
  Button,
  AccordionTableItem,
} from 'views/components'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function IndividualAccountForm(props) {
  // Destructure
  const { form, actions } = props

  // Hooks
  const [userCredentials] = useLocalStorage('userCredentials')
  const {
    createAccountIndividual,
    accountIndividualCreateData,
    accountIndividualCreateError,
    isAccountIndividualCreateLoading,
    isAccountIndividualCreateSuccess,
  } = useCreateAccountIndividual()
  const {
    updateAccountIndividual,
    accountIndividualUpdateData,
    accountIndividualUpdateError,
    isAccountIndividualUpdateLoading,
    isAccountIndividualUpdateSuccess,
  } = useUpdateAccountIndividual()

  // Store State
  const { toBeUpdatedAccount } = form

  // Store Actions
  const {
    setIsIndividualAccountDrawerOpen,
    setToBeUpdatedAccount,
    showAlert,
    setShowHeaderLoader,
    setCreatedAccount,
    setIsPageTableUpdated,
    setIsRecordUpdated,
  } = actions

  // Internal State
  const [isCreateAccount, setIsCreateAccount] = useState(true)
  const [initialData, setInitialData] = useState()
  const [isFreeFormExpanded, setIsFreeFormExpanded] = useState(true)
  const [isStructuredExpanded, setIsStructuredExpanded] = useState(false)
  const [isNationalIDExpanded, setIsNationalIDExpanded] = useState(false)

  // Variables
  const formRef = useRef()
  const initialValues = {
    reference_id: '',
    customer_id: '',
    is_customer: undefined,
    email: '',
    name_first: '',
    name_middle: '',
    name_last: '',
    date_of_birth: '',
    place_of_birth: '',
    nationality_country_code: '',
    address_line: '',
    address_street_no: '',
    address_street_name: '',
    address_building_name: '',
    address_floor: '',
    address_room: '',
    address_department: '',
    address_postbox: '',
    address_city_location: '',
    address_city: '',
    address_region: '',
    address_district: '',
    address_postcode: '',
    address_country_code: '',
    national_id_type_code: '',
    national_id: '',
    national_id_issue_country: '',
  }

  const validationSchema = Yup.object().shape({
    reference_id: Yup.string().max(50).required('Reference ID is required'),
    customer_id: Yup.string()
      .max(50)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      ),
    email: Yup.string().email().nullable(),
    name_first: Yup.string().max(50).nullable(),
    name_middle: Yup.string().max(50).nullable(),
    name_last: Yup.string().max(50).required('Last Name is required'),
    is_customer: Yup.bool().required('Specify if account is a customer'),
    date_of_birth: Yup.date()
      .max(new Date(Date.now() - 567648000000), 'Must be atleast 18 years')
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test(
        'date_of_birth_with_place',
        'Date of birth is required when place of birth is provided',
        (value, context) => {
          const placeOfBirth = context.parent.place_of_birth
          if (placeOfBirth && !value) {
            return false // Fail if place is provided but date isn't
          }
          return true
        },
      ),
    place_of_birth: Yup.string()
      .max(50)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test(
        'place_of_birth_with_date',
        'Place of birth is required when date of birth is provided',
        (value, context) => {
          const dateOfBirth = context.parent.date_of_birth
          if (dateOfBirth && !value) {
            return false // Fail if date is provided but place isn't
          }
          return true
        },
      ),
    nationality_country_code: Yup.string().max(2).nullable(),
    address_line: Yup.string()
      .max(256)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test(
        'address_line',
        'Required if street number, street name, and building name are not specified',
        (value, context) => hasRequiredAddressFields(context),
      ),
    address_street_no: Yup.string()
      .max(10)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test('address_street_no', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context),
      ),
    address_street_name: Yup.string()
      .max(100)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test('address_street_name', 'Required with building name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context),
      ),
    address_building_name: Yup.string()
      .max(20)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test('address_building_name', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context),
      ),
    address_floor: Yup.string().max(20).nullable(),
    address_room: Yup.string().max(20).nullable(),
    address_department: Yup.string().max(20).nullable(),
    address_postbox: Yup.string().max(20).nullable(),
    address_city_location: Yup.string().max(50).nullable(),
    address_city: Yup.string()
      .max(50)
      .nullable()
      .test('address_city', 'Required if address is provided', (value, context) => hasAddress(value, context)),
    address_region: Yup.string().max(50).nullable(),
    address_district: Yup.string().max(50).nullable(),
    address_postcode: Yup.string().max(25).nullable(),
    address_country_code: Yup.string()
      .max(2)
      .nullable()
      .test('address_country_code', 'Required if address is provided', (value, context) => hasAddress(value, context)),
    national_id_type_code: Yup.string()
      .max(10)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test(
        'national_id_type_required',
        'ID Type is required when National ID or Country Issuer is provided',
        (value, context) => {
          const hasNationalId = Boolean(context.parent.national_id)
          const hasIssueCountry = Boolean(context.parent.national_id_issue_country)

          // If any other National ID fields are provided, this field is required
          if ((hasNationalId || hasIssueCountry) && !value) {
            return false
          }
          return true
        },
      ),
    national_id: Yup.string()
      .max(35)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context),
      )
      .test(
        'national_id_required',
        'National ID is required when ID Type or Country Issuer is provided',
        (value, context) => {
          const hasIdType = Boolean(context.parent.national_id_type_code)
          const hasIssueCountry = Boolean(context.parent.national_id_issue_country)

          // If any other National ID fields are provided, this field is required
          if ((hasIdType || hasIssueCountry) && !value) {
            return false
          }
          return true
        },
      ),
    national_id_issue_country: Yup.string().max(2).nullable(),
  })

  // Functions
  const triggerSubmit = () => {
    formRef.current.submitForm()
  }

  const handleOnSubmit = (values) => {
    // Create a copy of values to modify
    const formValues = { ...values }

    // Check if any address fields are filled
    const hasAddressLine = Boolean(formValues.address_line)
    const hasStreetNo = Boolean(formValues.address_street_no)
    const hasStreetName = Boolean(formValues.address_street_name)
    const hasBuildingName = Boolean(formValues.address_building_name)

    // If any core address fields are provided, add address_type = 'GEOG'
    const hasAddress = hasAddressLine || (hasStreetName && (hasStreetNo || hasBuildingName))

    if (hasAddress) {
      formValues.address_type_code = 'GEOG'
    }

    const payload = {
      biz_id: userCredentials.Business_ID,
      ...formValues,
    }

    // removing empty fields on object
    Object.keys(payload).forEach((k) => payload[k] === '' && delete payload[k])

    if (isCreateAccount) {
      createAccountIndividual(payload)
    } else if (!isCreateAccount) {
      updateAccountIndividual({ account_id: toBeUpdatedAccount.id, values: payload })
    }
  }

  const handleLoadingChange = () => {
    if (isAccountIndividualCreateLoading || isAccountIndividualUpdateLoading) {
      setShowHeaderLoader(true)
    }
    if (!isAccountIndividualCreateLoading && !isAccountIndividualUpdateLoading) {
      setShowHeaderLoader(false)
      if (initialData) {
        if (isCreateAccount) {
          if (isAccountIndividualCreateSuccess) {
            setCreatedAccount(accountIndividualCreateData)
            setIsIndividualAccountDrawerOpen(false)
            setIsPageTableUpdated(true)
            showAlert({ type: 'success', message: 'Successfully created record' })
          } else if (!isAccountIndividualCreateSuccess) {
            if (typeof accountIndividualCreateError?.response?.data?.data === 'string') {
              showAlert({ type: 'error', message: accountIndividualCreateError?.response?.data?.data })
              return
            }
            showAlert({ type: 'error', message: 'An error occurred in creating record' })
          }
        } else if (!isCreateAccount) {
          if (isAccountIndividualUpdateSuccess) {
            setCreatedAccount(accountIndividualUpdateData)
            setToBeUpdatedAccount(null)
            setIsIndividualAccountDrawerOpen(false)
            setIsRecordUpdated(true)
            showAlert({ type: 'success', message: 'Successfully updated record' })
          } else if (!isAccountIndividualUpdateSuccess) {
            if (typeof accountIndividualUpdateError?.response?.data?.data === 'string') {
              showAlert({ type: 'error', message: accountIndividualUpdateError?.response?.data?.data })
              return
            }
            showAlert({ type: 'error', message: 'An error occurred in updating record' })
          }
        }
      }
    }
  }

  function hasRequiredAddressFields(context) {
    const parent = context?.parent

    /*
      If the form already has required account fields
      (date and place of birth, customer id, or national id),
      skip required address fields validation
    */
    const hasRequiredAccountFields = validateRequiredAccountFields(context)
    if (hasRequiredAccountFields) {
      return true
    }

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasAddress = hasAddressLine || (hasStreetName && (hasStreetNo || hasBuildingName))

    return hasAddress
  }

  function hasAddress(value, context) {
    const parent = context?.parent

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)

    // If no address info is provided, value is optional
    if (!hasAddressLine && !hasStreetNo && !hasStreetName && !hasBuildingName) {
      return true
    }

    // If we have validated address information, value is required
    return Boolean(value)
  }

  /*
    Validates if at least one of:
    - Date and Place of Birth
    - National ID
    - Address
    - Customer ID
    is provided.

    If an attempt to provide one of these identifiers is made,
    even if the identifier is incomplete
    (e.g. specifying incomplete address, national ID, or date and place of birth),
    the validation will pass.
  */
  function validateRequiredAccountFields(context) {
    const parent = context?.parent

    // Check if at least date or place of birth is provided
    // The validation here is more relaxed, since we also require the other field if one is provided
    const hasDateOfBirth = Boolean(parent?.date_of_birth)
    const hasPlaceOfBirth = Boolean(parent?.place_of_birth)
    const hasBirthInfo = hasDateOfBirth || hasPlaceOfBirth

    // Check if any national ID fields are provided
    // The validation here is more relaxed, since we also require the other field if one is provided
    const hasNationalIdType = Boolean(parent?.national_id_type_code)
    const hasNationalID = Boolean(parent?.national_id)
    const hasNationalIDIssueCountry = Boolean(parent?.national_id_issue_country)

    const hasNationalIDInfo = hasNationalIdType || hasNationalID || hasNationalIDIssueCountry

    // Check if customer ID is provided
    const hasCustomerID = Boolean(parent?.customer_id)

    // Check if any required address is provided
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasBuildingName = Boolean(parent?.address_building_name)

    const hasAddress = hasAddressLine || hasStreetName || hasStreetNo || hasBuildingName

    // Return true if at least one identifier method is provided
    return hasBirthInfo || hasNationalIDInfo || hasAddress || hasCustomerID
  }

  // UseEffects
  useEffect(() => {
    if (!toBeUpdatedAccount) {
      setIsCreateAccount(true)
      // TODO REMOVE HARD CODED VALUE
      setInitialData({ ...initialValues, address_type_code: 'GEOG' })
      setInitialData(initialValues)
    } else {
      setIsCreateAccount(false)
      setInitialData({ ...toBeUpdatedAccount, address_type_code: 'GEOG' })
    }
  }, [])
  useEffect(() => handleLoadingChange(), [isAccountIndividualCreateLoading, isAccountIndividualUpdateLoading])
  return (
    <FormWrapper>
      {initialData && (
        <Form
          formRef={formRef}
          initialValues={initialData}
          validationSchema={validationSchema}
          onSubmit={handleOnSubmit}
        >
          <FormHeader>
            <FormTitle>{isCreateAccount ? 'Create Individual Account' : 'Edit Individual Account'}</FormTitle>
            {(accountIndividualCreateError || accountIndividualUpdateData) && (
              <FormError>
                {accountIndividualCreateError?.response?.data?.message ||
                  accountIndividualUpdateData?.response?.data?.message}
              </FormError>
            )}
          </FormHeader>

          <FormContainer>
            <FormInputGroupItem>
              <FormTextField label="Reference ID*" name="reference_id" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Customer ID*" name="customer_id" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormSelect label="Is Customer*" name="is_customer" options={BOOLEAN_OPTIONS} />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Email" type="email" name="email" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="First Name" name="name_first" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Middle Name" name="name_middle" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Last Name*" name="name_last" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormDatePicker label="Birthdate" name="date_of_birth" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Place of Birth" name="place_of_birth" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormSearchSelect label="Nationality" name="nationality_country_code" options={COUNTRIES_LIST} />
            </FormInputGroupItem>

            <AccordionTableItem
              style={{ marginTop: '20px' }}
              setIsExpanded={setIsStructuredExpanded}
              expandPanel={isStructuredExpanded}
              button={
                <TableButtonWrapper>
                  <TableRowWrapper isExpanded={isStructuredExpanded}>Structured Address (preferred)</TableRowWrapper>
                </TableButtonWrapper>
              }
              panel={
                <TablePanelWrapper>
                  <FormInputGroupItem>
                    <FormTextField label="Street Number*" name="address_street_no" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Street Name*" name="address_street_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Building Name" name="address_building_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Floor" name="address_floor" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Room" name="address_room" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Department" name="address_department" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Postbox" name="address_postbox" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="City Location" name="address_city_location" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="City*" name="address_city" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Region" name="address_region" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="District" name="address_district" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Zipcode" name="address_postcode" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect label="Country*" name="address_country_code" options={COUNTRIES_LIST} />
                  </FormInputGroupItem>
                </TablePanelWrapper>
              }
            />
            <AccordionTableItem
              style={{ marginTop: '20px' }}
              setIsExpanded={(val) => {
                setIsFreeFormExpanded(val)
              }}
              expandPanel={isFreeFormExpanded}
              button={
                <TableButtonWrapper>
                  <TableRowWrapper isExpanded={isFreeFormExpanded}>Free Form Address</TableRowWrapper>
                </TableButtonWrapper>
              }
              panel={
                <TablePanelWrapper>
                  <FormInputGroupItem>
                    <FormTextArea label="Address Line" name="address_line" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="City" name="address_city" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect label="Country" name="address_country_code" options={COUNTRIES_LIST} />
                  </FormInputGroupItem>
                </TablePanelWrapper>
              }
            />
            <AccordionTableItem
              style={{ marginTop: '20px' }}
              setIsExpanded={(val) => {
                setIsNationalIDExpanded(val)
              }}
              expandPanel={isNationalIDExpanded}
              button={
                <TableButtonWrapper>
                  <TableRowWrapper isExpanded={isNationalIDExpanded}>National ID</TableRowWrapper>
                </TableButtonWrapper>
              }
              panel={
                <TablePanelWrapper>
                  <FormInputGroupItem>
                    <FormSelect
                      label="National ID Type"
                      name="national_id_type_code"
                      options={NATIONAL_ID_TYPE_INDIVIDUAL_OPTIONS}
                    />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="National ID" name="national_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect
                      label="National ID Country Issuer"
                      name="national_id_issue_country"
                      options={COUNTRIES_LIST}
                    />
                  </FormInputGroupItem>
                </TablePanelWrapper>
              }
            />
          </FormContainer>

          <FormFooter>
            <Button type="button" onClick={triggerSubmit}>
              {isCreateAccount ? 'Create Individual Account' : 'Save Individual Account'}
            </Button>
          </FormFooter>
        </Form>
      )}
    </FormWrapper>
  )
}

// Default Props
IndividualAccountForm.defaultProps = {
  formRef: {},
  form: {},
  actions: {},
}

// Proptypes Validation
IndividualAccountForm.propTypes = {
  formRef: PropTypes.shape({ root: PropTypes.string }),
  form: PropTypes.shape({
    COUNTRIES_LIST: PropTypes.instanceOf(Array),
    toBeUpdatedAccount: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  actions: PropTypes.shape({
    setCountryListOptions: PropTypes.func,
    setIsIndividualAccountDrawerOpen: PropTypes.func,
    setToBeUpdatedAccount: PropTypes.func,
    setIsPageTableUpdated: PropTypes.func,
    showAlert: PropTypes.func,
    setShowHeaderLoader: PropTypes.func,
    setCreatedAccount: PropTypes.func,
    setIsRecordUpdated: PropTypes.func,
  }),
}

export default connect(mapStateToProps, mapDispatchToProps)(IndividualAccountForm)
